/**
 * Subtitle My Speech Tutorial Page (Me mode, subtitles only, desktop app + KizunaAI)
 *
 * Screenshots were taken from the Sokuji v0.44.0 desktop app on Windows; the ones
 * not retaken for v0.44.0 are from v0.41.1.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  pageTitle: 'Live Translated Subtitles for Your Talks, Streams and Presentations',
  backLink: 'Back to KizunaAI Quick Start',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Overview',
    content:
      'This guide sets up the Sokuji desktop app to turn what you say into translated subtitles for your audience: a talk, a live stream, an online presentation or a lesson. No synthetic voice is generated, and you need no virtual audio cable. The subtitles appear in a floating window that OBS and screen sharing can capture. It uses KizunaAI (Powered by Soniox; the Qwen engine works the same way). The screenshots are from Sokuji 0.44.0 on Windows, speaking Chinese with English subtitles.',
  },
  steps: [
    {
      title: 'Set up Sokuji with KizunaAI',
      content:
        'If you have not done so yet, follow the <a href="/docs/kizuna-ai/quick-start">KizunaAI quick start</a> to install Sokuji and sign in.',
    },
    {
      title: 'Choose “Subtitle my own speech”',
      content:
        'In the setup guide, choose <strong>Subtitle my own speech</strong>. It puts Sokuji in <strong>Me</strong> mode with subtitles only. If you have already finished the setup, click <strong>Me</strong> in the mode switch at the bottom left of the main window and, in the <strong>Speech</strong> block of Settings, switch off <strong>Translation the other side hears</strong>.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Set the two languages',
      content:
        'Set <strong>I speak</strong> to the language you talk in and <strong>they read</strong> to the language of the subtitles. Here: I speak Chinese, they read English.',
      screenshot: '02-languages.png',
    },
    {
      title: 'Choose your microphone',
      content: 'In Settings, under <strong>Microphone</strong>, pick the microphone you speak into.',
      screenshot: '03-microphone.png',
    },
    {
      title: 'Try it in the main window',
      content:
        'Click <strong>Start</strong> and say a few sentences. Sokuji shows what you said and its translation as subtitles in the main window. Nothing is spoken aloud.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Open the subtitle window',
      content:
        'Click <strong>Subtitle</strong> in the title bar to turn Sokuji into a slim subtitle window. Move and resize it, then click <strong>Start translating</strong>. The <strong>Both</strong> button in its toolbar switches between showing both lines, only the original (<strong>Src</strong>) or only the translation (<strong>Trans</strong>).',
      screenshot: '05-subtitle-window.png',
    },
    {
      title: 'Show the subtitles to your audience',
      content:
        'In OBS, add a <strong>Window Capture</strong> source and choose the Sokuji subtitle window. In a video call, share your screen or that window. Your audience sees the subtitles without installing anything.',
    },
  ],
  tips: {
    title: 'Tips for Best Results',
    items: [
      'Speak in complete sentences and pause briefly at the end of each: the translation appears once you finish a thought.',
      'Show only the translation (Trans) if your audience does not read your language.',
      'Use the download button in the toolbar to save the transcript after your talk.',
    ],
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'Do I need VB-CABLE for this?',
        answer: 'No. The virtual audio cable is only needed when others should hear a translated voice. Here your audience reads the translation.',
      },
      {
        question: 'Does my audience need Sokuji?',
        answer: 'No. They see the subtitles in your stream, your recording or your shared screen.',
      },
      {
        question: 'Why subtitles only instead of a translated voice?',
        answer: 'Your audience keeps hearing your real voice, and generating speech costs extra. If you would rather have a voice, follow the guide for being understood in a meeting.',
      },
    ],
  },
  troubleshooting: {
    title: 'Troubleshooting',
    content:
      'Still stuck? Send us feedback from the account menu, or write to <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. You can also ask in <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const zh: TutorialData = {
  pageTitle: '为你的演讲、直播和演示提供实时翻译字幕',
  backLink: '返回 KizunaAI 快速入门',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概述',
    content: '本指南介绍如何设置 Sokuji 桌面应用，把你说的话变成给观众看的翻译字幕：适用于演讲、直播、在线演示或授课。不会生成合成语音，也不需要虚拟音频线。字幕显示在一个浮动窗口中，OBS 和屏幕共享都可以捕获它。本指南使用 KizunaAI（Soniox 驱动；Qwen 引擎的用法相同）。截图来自 Windows 上的 Sokuji 0.44.0，说中文，配英文字幕。',
  },
  steps: [
    {
      title: '用 KizunaAI 设置 Sokuji',
      content: '如果还没有完成，请按照 <a href="/docs/kizuna-ai/quick-start">KizunaAI 快速入门</a>安装 Sokuji 并登录。',
    },
    {
      title: '选择“给我的发言配字幕”',
      content: '在设置向导中选择<strong>给我的发言配字幕</strong>。它会让 Sokuji 进入<strong>我</strong>模式，并且只显示字幕。如果已经完成设置，请点击主窗口左下角模式切换中的<strong>我</strong>，并在设置的<strong>语音</strong>部分关闭<strong>对方听到的翻译</strong>。',
      screenshot: '01-scenario.png',
    },
    {
      title: '设置两种语言',
      content: '将<strong>我说</strong>设为你说话所用的语言，将<strong>对方读到</strong>设为字幕的语言。此处：我说中文，对方读到英语。',
      screenshot: '02-languages.png',
    },
    {
      title: '选择麦克风',
      content: '在设置的<strong>麦克风</strong>下，选择你说话用的麦克风。',
      screenshot: '03-microphone.png',
    },
    {
      title: '先在主窗口中试一试',
      content: '点击<strong>开始</strong>并说几句话。Sokuji 会在主窗口中以字幕显示你说的话及其翻译，不会朗读任何内容。',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: '打开字幕窗口',
      content: '点击标题栏中的<strong>字幕</strong>，Sokuji 会变成一个细长的字幕窗口。移动并调整它的大小，然后点击<strong>开始翻译</strong>。工具栏中的<strong>两者</strong>按钮用于切换显示方式：同时显示两行、只显示原话（<strong>原文</strong>）或只显示翻译（<strong>译文</strong>）。',
      screenshot: '05-subtitle-window.png',
    },
    {
      title: '把字幕展示给观众',
      content: '在 OBS 中添加一个<strong>窗口采集</strong>来源，并选择 Sokuji 字幕窗口。在视频通话中，共享你的屏幕或该窗口。观众无需安装任何东西就能看到字幕。',
    },
  ],
  tips: {
    title: '最佳使用建议',
    items: [
      '说完整的句子，并在每句话结束时稍作停顿：一段话说完后，翻译才会出现。',
      '如果观众看不懂你的语言，可以切换到“译文”，只显示翻译。',
      '演讲结束后，可以用工具栏中的下载按钮保存文字记录。',
    ],
  },
  faq: {
    title: '常见问题',
    items: [
      {
        question: '这需要 VB-CABLE 吗？',
        answer: '不需要。只有在需要让别人听到翻译语音时才需要虚拟音频线。此处观众阅读的是翻译。',
      },
      {
        question: '观众需要安装 Sokuji 吗？',
        answer: '不需要。他们会在你的直播、录像或共享屏幕中看到字幕。',
      },
      {
        question: '为什么只用字幕，而不用翻译语音？',
        answer: '观众仍能听到你的真实声音，而且生成语音需要额外费用。如果你更想要语音，请按照“在会议中用另一种语言发言”指南操作。',
      },
    ],
  },
  troubleshooting: {
    title: '故障排除',
    content: '仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>。你也可以在 <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> 中提问。',
  },
};

const ja: TutorialData = {
  pageTitle: '講演・配信・プレゼンにリアルタイムの翻訳字幕を付ける',
  backLink: 'KizunaAI クイックスタートに戻る',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概要',
    content: 'このガイドでは、あなたの話を聴衆向けの翻訳字幕にするように Sokuji デスクトップアプリを設定します。講演、ライブ配信、オンラインプレゼン、授業などに使えます。合成音声は生成されず、仮想オーディオケーブルも不要です。字幕はフローティングウィンドウに表示され、OBS や画面共有でキャプチャできます。KizunaAI（Soniox 提供。Qwen エンジンでも同じ手順です）を使用します。スクリーンショットは Windows 版 Sokuji 0.44.0 のもので、中国語で話し、英語の字幕を表示しています。',
  },
  steps: [
    {
      title: 'KizunaAI で Sokuji をセットアップする',
      content: 'まだの場合は、<a href="/docs/kizuna-ai/quick-start">KizunaAI クイックスタート</a>に沿って Sokuji をインストールし、サインインしてください。',
    },
    {
      title: '「自分の話に字幕を付ける」を選ぶ',
      content: 'セットアップガイドで<strong>自分の話に字幕を付ける</strong>を選びます。Sokuji が字幕のみの<strong>自分</strong>モードになります。すでにセットアップを終えている場合は、メイン画面左下のモード切り替えで<strong>自分</strong>をクリックし、設定の<strong>音声</strong>セクションで<strong>相手に聞こえる翻訳</strong>をオフにします。',
      screenshot: '01-scenario.png',
    },
    {
      title: '2 つの言語を設定する',
      content: '<strong>話す言語</strong>を自分が話す言語に、<strong>相手が読む言語</strong>を字幕の言語に設定します。ここでは、話す言語は中国語、相手が読む言語は英語です。',
      screenshot: '02-languages.png',
    },
    {
      title: 'マイクを選ぶ',
      content: '設定の<strong>マイクロフォン</strong>で、話すときに使うマイクを選びます。',
      screenshot: '03-microphone.png',
    },
    {
      title: 'メイン画面で試す',
      content: '<strong>開始</strong>をクリックして、いくつか文を話してみます。Sokuji はあなたの発言とその翻訳をメイン画面に字幕で表示します。音声での読み上げはありません。',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: '字幕ウィンドウを開く',
      content: 'タイトルバーの<strong>字幕</strong>をクリックすると、Sokuji が細長い字幕ウィンドウに切り替わります。位置とサイズを調整してから、<strong>翻訳を開始</strong>をクリックします。ツールバーの<strong>両方</strong>ボタンで、両方の行を表示するか、原文だけ（<strong>原文</strong>）か訳文だけ（<strong>訳文</strong>）を表示するかを切り替えられます。',
      screenshot: '05-subtitle-window.png',
    },
    {
      title: '聴衆に字幕を見せる',
      content: 'OBS で<strong>ウィンドウキャプチャ</strong>ソースを追加し、Sokuji の字幕ウィンドウを選びます。ビデオ通話では、画面またはそのウィンドウを共有します。聴衆は何もインストールせずに字幕を見られます。',
    },
  ],
  tips: {
    title: '最良の結果を得るためのヒント',
    items: [
      '完結した文で話し、文の終わりで少し間を置いてください。ひとまとまりの話を終えた時点で翻訳が表示されます。',
      '聴衆があなたの言語を読めない場合は、「訳文」に切り替えて翻訳だけを表示してください。',
      '講演が終わったら、ツールバーのダウンロードボタンで文字起こしを保存できます。',
    ],
  },
  faq: {
    title: 'よくある質問',
    items: [
      {
        question: 'VB-CABLE は必要ですか？',
        answer: 'いいえ。仮想オーディオケーブルが必要なのは、翻訳された音声を相手に聞かせるときだけです。ここでは聴衆は翻訳を読みます。',
      },
      {
        question: '聴衆に Sokuji は必要ですか？',
        answer: 'いいえ。聴衆は配信、録画、または共有画面で字幕を見ます。',
      },
      {
        question: '翻訳音声ではなく字幕だけにするのはなぜですか？',
        answer: '聴衆はあなたの生の声を聞き続けられ、音声の生成には追加の費用がかかるからです。音声を使いたい場合は、会議で別の言語で話すためのガイドに沿って設定してください。',
      },
    ],
  },
  troubleshooting: {
    title: 'トラブルシューティング',
    content: 'それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href="mailto:support@kizuna.ai">support@kizuna.ai</a> までご連絡ください。<a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> で質問することもできます。',
  },
};

const ko: TutorialData = {
  pageTitle: '강연, 방송, 발표를 위한 실시간 번역 자막',
  backLink: 'KizunaAI 빠른 시작으로 돌아가기',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '개요',
    content: '이 가이드는 내가 하는 말을 청중을 위한 번역 자막으로 바꾸도록 Sokuji 데스크톱 앱을 설정합니다. 강연, 라이브 방송, 온라인 발표, 수업 등에 쓸 수 있습니다. 합성 음성은 생성되지 않으며 가상 오디오 케이블도 필요 없습니다. 자막은 OBS와 화면 공유로 캡처할 수 있는 떠 있는 창에 표시됩니다. KizunaAI(Soniox 제공이며, Qwen 엔진도 같은 방식으로 작동합니다)를 사용합니다. 스크린샷은 Windows용 Sokuji 0.44.0에서 촬영했으며, 중국어로 말하고 영어 자막을 표시하는 예입니다.',
  },
  steps: [
    {
      title: 'KizunaAI로 Sokuji 설정',
      content: '아직 하지 않았다면 <a href="/docs/kizuna-ai/quick-start">KizunaAI 빠른 시작</a>을 따라 Sokuji를 설치하고 로그인하세요.',
    },
    {
      title: '“내 말에 자막 달기” 선택',
      content: '설정 가이드에서 <strong>내 말에 자막 달기</strong>를 선택하세요. Sokuji가 자막만 표시하는 <strong>나</strong> 모드로 설정됩니다. 이미 설정을 마쳤다면 메인 창 왼쪽 아래의 모드 전환에서 <strong>나</strong>를 클릭하고, 설정의 <strong>음성</strong> 섹션에서 <strong>상대방에게 들리는 번역</strong>을 끄세요.',
      screenshot: '01-scenario.png',
    },
    {
      title: '두 언어 설정',
      content: '<strong>내가 말하는 언어</strong>를 내가 말할 언어로, <strong>상대방이 읽는 언어</strong>를 자막 언어로 설정하세요. 여기서는 내가 말하는 언어는 중국어, 상대방이 읽는 언어는 영어입니다.',
      screenshot: '02-languages.png',
    },
    {
      title: '마이크 선택',
      content: '설정의 <strong>마이크</strong>에서 말할 때 쓰는 마이크를 고르세요.',
      screenshot: '03-microphone.png',
    },
    {
      title: '메인 창에서 먼저 해 보기',
      content: '<strong>시작</strong>을 클릭하고 몇 문장을 말해 보세요. Sokuji가 내가 한 말과 그 번역을 메인 창에 자막으로 보여 줍니다. 음성으로 읽어 주지는 않습니다.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: '자막 창 열기',
      content: '제목 표시줄의 <strong>자막</strong>을 클릭하면 Sokuji가 얇은 자막 창으로 바뀝니다. 창을 옮기고 크기를 조절한 다음 <strong>번역 시작</strong>을 클릭하세요. 도구 모음의 <strong>모두</strong> 버튼으로 두 줄을 모두 표시할지, 원문만(<strong>원문</strong>) 또는 번역만(<strong>번역</strong>) 표시할지 전환할 수 있습니다.',
      screenshot: '05-subtitle-window.png',
    },
    {
      title: '청중에게 자막 보여 주기',
      content: 'OBS에서 <strong>Window Capture</strong> 소스를 추가하고 Sokuji 자막 창을 선택하세요. 화상 통화에서는 화면이나 그 창을 공유하세요. 청중은 아무것도 설치하지 않고 자막을 볼 수 있습니다.',
    },
  ],
  tips: {
    title: '최상의 결과를 위한 팁',
    items: [
      '완전한 문장으로 말하고 각 문장이 끝날 때 잠깐 멈추세요. 하나의 생각을 마쳐야 번역이 나타납니다.',
      '청중이 내 언어를 읽지 못한다면 “번역”으로 전환해 번역만 표시하세요.',
      '강연이 끝나면 도구 모음의 다운로드 버튼으로 대화 기록을 저장하세요.',
    ],
  },
  faq: {
    title: '자주 묻는 질문',
    items: [
      {
        question: 'VB-CABLE이 필요한가요?',
        answer: '아니요. 가상 오디오 케이블은 다른 사람이 번역된 음성을 들어야 할 때만 필요합니다. 여기서는 청중이 번역을 읽습니다.',
      },
      {
        question: '청중에게 Sokuji가 필요한가요?',
        answer: '아니요. 청중은 방송, 녹화 영상 또는 공유 화면에서 자막을 봅니다.',
      },
      {
        question: '왜 번역 음성 대신 자막만 쓰나요?',
        answer: '청중이 내 실제 목소리를 계속 들을 수 있고, 음성 생성에는 추가 비용이 들기 때문입니다. 음성을 원한다면 회의에서 다른 언어로 말하기 가이드를 따르세요.',
      },
    ],
  },
  troubleshooting: {
    title: '문제 해결',
    content: '그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>에서 질문할 수도 있습니다.',
  },
};

const es: TutorialData = {
  pageTitle: 'Subtítulos traducidos en directo para tus charlas, directos y presentaciones',
  backLink: 'Volver al inicio rápido de KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Descripción general',
    content: 'Esta guía configura la aplicación de escritorio de Sokuji para convertir lo que dices en subtítulos traducidos para tu público: una charla, una retransmisión en directo, una presentación en línea o una clase. No se genera ninguna voz sintética y no necesitas un cable de audio virtual. Los subtítulos aparecen en una ventana flotante que OBS y la pantalla compartida pueden capturar. Usa KizunaAI (Con tecnología de Soniox; el motor de Qwen funciona igual). Las capturas son de Sokuji 0.44.0 en Windows, hablando en chino con subtítulos en inglés.',
  },
  steps: [
    {
      title: 'Configura Sokuji con KizunaAI',
      content: 'Si aún no lo has hecho, sigue el <a href="/docs/kizuna-ai/quick-start">inicio rápido de KizunaAI</a> para instalar Sokuji e iniciar sesión.',
    },
    {
      title: 'Elige «Subtitular lo que digo»',
      content: 'En la guía de configuración, elige <strong>Subtitular lo que digo</strong>. Pone Sokuji en modo <strong>Yo</strong>, solo con subtítulos. Si ya terminaste la configuración, haz clic en <strong>Yo</strong> en el selector de modo, abajo a la izquierda de la ventana principal, y, en la sección <strong>Voz</strong> de Configuración, desactiva <strong>Traducción que oye la otra parte</strong>.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Configura los dos idiomas',
      content: 'Pon en <strong>Yo hablo</strong> el idioma en el que hablas y en <strong>ellos leen</strong> el idioma de los subtítulos. Aquí: Yo hablo Chino, ellos leen Inglés.',
      screenshot: '02-languages.png',
    },
    {
      title: 'Elige tu micrófono',
      content: 'En Configuración, en <strong>Micrófono</strong>, elige el micrófono por el que hablas.',
      screenshot: '03-microphone.png',
    },
    {
      title: 'Pruébalo en la ventana principal',
      content: 'Haz clic en <strong>Iniciar</strong> y di unas cuantas frases. Sokuji muestra lo que dijiste y su traducción como subtítulos en la ventana principal. No se pronuncia nada en voz alta.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Abre la ventana de subtítulos',
      content: 'Haz clic en <strong>Subtítulos</strong> en la barra de título para convertir Sokuji en una ventana de subtítulos estrecha. Muévela y cambia su tamaño, y haz clic en <strong>Empezar a traducir</strong>. El botón <strong>Ambos</strong> de su barra de herramientas alterna entre mostrar las dos líneas, solo el original (<strong>Origen</strong>) o solo la traducción (<strong>Traducción</strong>).',
      screenshot: '05-subtitle-window.png',
    },
    {
      title: 'Muestra los subtítulos a tu público',
      content: 'En OBS, añade una fuente <strong>Captura de ventana</strong> y elige la ventana de subtítulos de Sokuji. En una videollamada, comparte tu pantalla o esa ventana. Tu público ve los subtítulos sin instalar nada.',
    },
  ],
  tips: {
    title: 'Consejos para obtener mejores resultados',
    items: [
      'Habla con frases completas y haz una breve pausa al final de cada una: la traducción aparece cuando terminas una idea.',
      'Muestra solo la traducción (Traducción) si tu público no lee tu idioma.',
      'Después de tu charla, usa el botón de descarga de la barra de herramientas para guardar la transcripción.',
    ],
  },
  faq: {
    title: 'Preguntas frecuentes',
    items: [
      {
        question: '¿Necesito VB-CABLE para esto?',
        answer: 'No. El cable de audio virtual solo hace falta cuando los demás deben oír una voz traducida. Aquí tu público lee la traducción.',
      },
      {
        question: '¿Mi público necesita Sokuji?',
        answer: 'No. Ve los subtítulos en tu retransmisión, tu grabación o tu pantalla compartida.',
      },
      {
        question: '¿Por qué solo subtítulos en lugar de una voz traducida?',
        answer: 'Tu público sigue oyendo tu voz real, y generar voz cuesta más. Si prefieres una voz, sigue la guía para hablar en reuniones en otro idioma.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solución de problemas',
    content: '¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. También puedes preguntar en <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const fr: TutorialData = {
  pageTitle: 'Sous-titres traduits en direct pour vos conférences, streams et présentations',
  backLink: 'Retour au démarrage rapide KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Aperçu',
    content: 'Ce guide configure l’application de bureau Sokuji pour transformer ce que vous dites en sous-titres traduits pour votre public : une conférence, un stream en direct, une présentation en ligne ou un cours. Aucune voix de synthèse n’est générée, et vous n’avez besoin d’aucun câble audio virtuel. Les sous-titres s’affichent dans une fenêtre flottante qu’OBS et le partage d’écran peuvent capturer. Il utilise KizunaAI (Propulsé par Soniox ; le moteur Qwen fonctionne de la même façon). Les captures d’écran proviennent de Sokuji 0.44.0 sous Windows, en parlant chinois avec des sous-titres en anglais.',
  },
  steps: [
    {
      title: 'Configurez Sokuji avec KizunaAI',
      content: 'Si ce n’est pas encore fait, suivez le <a href="/docs/kizuna-ai/quick-start">démarrage rapide KizunaAI</a> pour installer Sokuji et vous connecter.',
    },
    {
      title: 'Choisissez « Sous-titrer mon propre discours »',
      content: 'Dans le guide de configuration, choisissez <strong>Sous-titrer mon propre discours</strong>. Sokuji passe alors en mode <strong>Moi</strong>, avec des sous-titres uniquement. Si vous avez déjà terminé la configuration, cliquez sur <strong>Moi</strong> dans le sélecteur de mode, en bas à gauche de la fenêtre principale, puis, dans la section <strong>Voix</strong> des Paramètres, désactivez <strong>Traduction entendue par l\'autre</strong>.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Réglez les deux langues',
      content: 'Réglez <strong>je parle</strong> sur la langue dans laquelle vous parlez et <strong>ils lisent</strong> sur la langue des sous-titres. Ici : je parle chinois, ils lisent anglais.',
      screenshot: '02-languages.png',
    },
    {
      title: 'Choisissez votre microphone',
      content: 'Dans les Paramètres, sous <strong>Microphone</strong>, sélectionnez le microphone dans lequel vous parlez.',
      screenshot: '03-microphone.png',
    },
    {
      title: 'Essayez dans la fenêtre principale',
      content: 'Cliquez sur <strong>Démarrer</strong> et dites quelques phrases. Sokuji affiche ce que vous avez dit et sa traduction en sous-titres dans la fenêtre principale. Rien n’est prononcé à voix haute.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Ouvrez la fenêtre de sous-titres',
      content: 'Cliquez sur <strong>Sous-titres</strong> dans la barre de titre pour transformer Sokuji en une fine fenêtre de sous-titres. Déplacez-la et redimensionnez-la, puis cliquez sur <strong>Démarrer la traduction</strong>. Le bouton <strong>Les deux</strong> de sa barre d’outils permet de basculer entre les deux lignes, uniquement l’original (<strong>Source</strong>) ou uniquement la traduction (<strong>Traduction</strong>).',
      screenshot: '05-subtitle-window.png',
    },
    {
      title: 'Montrez les sous-titres à votre public',
      content: 'Dans OBS, ajoutez une source <strong>Capture de fenêtre</strong> et choisissez la fenêtre de sous-titres de Sokuji. Dans un appel vidéo, partagez votre écran ou cette fenêtre. Votre public voit les sous-titres sans rien installer.',
    },
  ],
  tips: {
    title: 'Conseils pour de meilleurs résultats',
    items: [
      'Parlez en phrases complètes et marquez une courte pause à la fin de chacune : la traduction s’affiche une fois votre idée terminée.',
      'Affichez uniquement la traduction (Traduction) si votre public ne lit pas votre langue.',
      'Après votre intervention, utilisez le bouton de téléchargement de la barre d’outils pour enregistrer la transcription.',
    ],
  },
  faq: {
    title: 'Questions fréquentes',
    items: [
      {
        question: 'Ai-je besoin de VB-CABLE pour cela ?',
        answer: 'Non. Le câble audio virtuel n’est nécessaire que lorsque les autres doivent entendre une voix traduite. Ici, votre public lit la traduction.',
      },
      {
        question: 'Mon public a-t-il besoin de Sokuji ?',
        answer: 'Non. Il voit les sous-titres dans votre stream, votre enregistrement ou votre écran partagé.',
      },
      {
        question: 'Pourquoi des sous-titres uniquement plutôt qu’une voix traduite ?',
        answer: 'Votre public continue d’entendre votre vraie voix, et la génération de voix coûte plus cher. Si vous préférez une voix, suivez le guide pour parler en réunion dans une autre langue.',
      },
    ],
  },
  troubleshooting: {
    title: 'Dépannage',
    content: 'Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ar: TutorialData = {
  pageTitle: 'ترجمة نصية مباشرة لمحاضراتك وبثّك وعروضك التقديمية',
  backLink: 'العودة إلى البدء السريع مع KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'نظرة عامة',
    content: 'يُعدّ هذا الدليل تطبيق Sokuji لسطح المكتب لتحويل ما تقوله إلى نص مترجَم لجمهورك في صورة ترجمة نصية: محاضرة أو بث مباشر أو عرض تقديمي عبر الإنترنت أو درس. لا يُولَّد أي صوت اصطناعي، ولا تحتاج إلى كابل صوت افتراضي. تظهر الترجمة النصية في نافذة عائمة يمكن لـ OBS ولمشاركة الشاشة التقاطها. يستخدم الدليل KizunaAI (مدعوم من Soniox؛ ويعمل محرك Qwen بالطريقة نفسها). لقطات الشاشة مأخوذة من Sokuji 0.44.0 على Windows، مع التحدث بالصينية وترجمة نصية بالإنجليزية.',
  },
  steps: [
    {
      title: 'اضبط Sokuji مع KizunaAI',
      content: 'إذا لم تفعل ذلك بعد، فاتبع <a href="/docs/kizuna-ai/quick-start">البدء السريع مع KizunaAI</a> لتثبيت Sokuji وتسجيل الدخول.',
    },
    {
      title: 'اختر «ترجمة كلامي إلى نص»',
      content: 'في دليل الإعداد، اختر <strong>ترجمة كلامي إلى نص</strong>. يضع ذلك Sokuji في وضع <strong>أنا</strong> مع ترجمة نصية فقط. إذا كنت قد أنهيت الإعداد بالفعل، فانقر على <strong>أنا</strong> في مبدّل الأوضاع أسفل يسار النافذة الرئيسية، وفي قسم <strong>الكلام</strong> في الإعدادات، أوقف <strong>الترجمة التي يسمعها الطرف الآخر</strong>.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'اضبط اللغتين',
      content: 'اضبط <strong>أتحدث</strong> على اللغة التي تتحدث بها و<strong>يقرأون</strong> على لغة الترجمة النصية. هنا: أتحدث الصينية، يقرأون الإنجليزية.',
      screenshot: '02-languages.png',
    },
    {
      title: 'اختر الميكروفون',
      content: 'في الإعدادات، تحت <strong>ميكروفون</strong>، اختر الميكروفون الذي تتحدث فيه.',
      screenshot: '03-microphone.png',
    },
    {
      title: 'جرّبه في النافذة الرئيسية',
      content: 'انقر على <strong>ابدأ</strong> وقل بضع جمل. يعرض Sokuji ما قلته وترجمته كترجمة نصية في النافذة الرئيسية. لا يُنطق أي شيء بصوت مسموع.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'افتح نافذة الترجمة النصية',
      content: 'انقر على <strong>ترجمة</strong> في شريط العنوان ليتحوّل Sokuji إلى نافذة ترجمة نصية نحيفة. حرّكها وغيّر حجمها، ثم انقر على <strong>بدء الترجمة</strong>. يبدّل زر <strong>الكل</strong> في شريط أدواتها بين عرض السطرين معًا، أو الأصل فقط (<strong>الأصل</strong>)، أو الترجمة فقط (<strong>الترجمة</strong>).',
      screenshot: '05-subtitle-window.png',
    },
    {
      title: 'اعرض الترجمة النصية على جمهورك',
      content: 'في OBS، أضف مصدر <strong>Window Capture</strong> واختر نافذة الترجمة النصية في Sokuji. في مكالمة فيديو، شارك شاشتك أو تلك النافذة. يرى جمهورك الترجمة النصية دون تثبيت أي شيء.',
    },
  ],
  tips: {
    title: 'نصائح لأفضل النتائج',
    items: [
      'تحدّث بجمل كاملة وتوقف لحظة في نهاية كل منها: تظهر الترجمة بعد أن تُكمل فكرتك.',
      'اعرض الترجمة فقط (الترجمة) إذا كان جمهورك لا يقرأ لغتك.',
      'بعد انتهاء حديثك، استخدم زر التنزيل في شريط الأدوات لحفظ النص المكتوب.',
    ],
  },
  faq: {
    title: 'الأسئلة الشائعة',
    items: [
      {
        question: 'هل أحتاج إلى VB-CABLE لهذا؟',
        answer: 'لا. لا تحتاج إلى كابل الصوت الافتراضي إلا عندما يجب أن يسمع الآخرون صوتًا مترجَمًا. هنا يقرأ جمهورك الترجمة.',
      },
      {
        question: 'هل يحتاج جمهوري إلى Sokuji؟',
        answer: 'لا. يرون الترجمة النصية في بثّك أو تسجيلك أو شاشتك المشتركة.',
      },
      {
        question: 'لماذا ترجمة نصية فقط بدلًا من صوت مترجَم؟',
        answer: 'يظل جمهورك يسمع صوتك الحقيقي، وتوليد الكلام يكلّف أكثر. إذا كنت تفضّل صوتًا، فاتبع دليل التحدث في الاجتماعات بلغة أخرى.',
      },
    ],
  },
  troubleshooting: {
    title: 'استكشاف الأخطاء وإصلاحها',
    content: 'هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const pt: TutorialData = {
  pageTitle: 'Legendas traduzidas ao vivo para suas palestras, transmissões e apresentações',
  backLink: 'Voltar ao início rápido com KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Visão geral',
    content:
      'Este guia configura o aplicativo de desktop do Sokuji para transformar o que você diz em legendas traduzidas para o seu público: uma palestra, uma transmissão ao vivo, uma apresentação on-line ou uma aula. Nenhuma voz sintética é gerada, e você não precisa de cabo de áudio virtual. As legendas aparecem em uma janela flutuante que o OBS e o compartilhamento de tela conseguem capturar. Ele usa a KizunaAI (Com tecnologia Soniox; o mesmo vale para o mecanismo Qwen). As capturas de tela são do Sokuji 0.44.0 no Windows, falando chinês com legendas em inglês.',
  },
  steps: [
    {
      title: 'Configure o Sokuji com a KizunaAI',
      content:
        'Se ainda não fez isso, siga o <a href="/docs/kizuna-ai/quick-start">início rápido com KizunaAI</a> para instalar o Sokuji e entrar na sua conta.',
    },
    {
      title: 'Escolha “Legendar minha própria fala”',
      content:
        'No guia de configuração, escolha <strong>Legendar minha própria fala</strong>. Isso coloca o Sokuji no modo <strong>Eu</strong>, só com legendas. Se você já concluiu a configuração, clique em <strong>Eu</strong> no seletor de modo, no canto inferior esquerdo da janela principal, e, na seção <strong>Voz</strong> das Configurações, desative <strong>Tradução que o outro lado ouve</strong>.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Defina os dois idiomas',
      content:
        'Defina <strong>eu falo</strong> como o idioma em que você fala e <strong>eles leem</strong> como o idioma das legendas. Aqui: eu falo Chinês, eles leem Inglês.',
      screenshot: '02-languages.png',
    },
    {
      title: 'Escolha seu microfone',
      content: 'Em Configurações, em <strong>Microfone</strong>, escolha o microfone em que você fala.',
      screenshot: '03-microphone.png',
    },
    {
      title: 'Experimente na janela principal',
      content:
        'Clique em <strong>Iniciar</strong> e diga algumas frases. O Sokuji mostra o que você disse e a tradução como legendas na janela principal. Nada é falado em voz alta.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Abra a janela de legendas',
      content:
        'Clique em <strong>Legendas</strong> na barra de título para transformar o Sokuji em uma janela de legendas compacta. Mova e redimensione a janela e clique em <strong>Iniciar tradução</strong>. O botão <strong>Ambos</strong> na barra de ferramentas dela alterna entre mostrar as duas linhas, só o original (<strong>Original</strong>) ou só a tradução (<strong>Tradução</strong>).',
      screenshot: '05-subtitle-window.png',
    },
    {
      title: 'Mostre as legendas ao seu público',
      content:
        'No OBS, adicione uma fonte <strong>Captura de janela</strong> e escolha a janela de legendas do Sokuji. Em uma videochamada, compartilhe sua tela ou essa janela. Seu público vê as legendas sem instalar nada.',
    },
  ],
  tips: {
    title: 'Dicas para melhores resultados',
    items: [
      'Fale em frases completas e faça uma pausa breve no fim de cada uma: a tradução aparece quando você conclui uma ideia.',
      'Mostre só a tradução (Tradução) se o seu público não lê o seu idioma.',
      'Use o botão de download na barra de ferramentas para salvar a transcrição depois da sua apresentação.',
    ],
  },
  faq: {
    title: 'Perguntas frequentes',
    items: [
      {
        question: 'Preciso do VB-CABLE para isso?',
        answer: 'Não. O cabo de áudio virtual só é necessário quando os outros precisam ouvir uma voz traduzida. Aqui o seu público lê a tradução.',
      },
      {
        question: 'Meu público precisa do Sokuji?',
        answer: 'Não. Ele vê as legendas na sua transmissão, na sua gravação ou na sua tela compartilhada.',
      },
      {
        question: 'Por que só legendas, em vez de uma voz traduzida?',
        answer: 'Seu público continua ouvindo a sua voz real, e gerar fala tem custo extra. Se preferir uma voz, siga o guia para ser entendido em uma reunião.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solução de problemas',
    content:
      'Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Você também pode perguntar no <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ru: TutorialData = {
  pageTitle: 'Переведённые субтитры в реальном времени для ваших выступлений, трансляций и презентаций',
  backLink: 'Назад к быстрому старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Обзор',
    content:
      'Это руководство настраивает настольное приложение Sokuji так, чтобы ваша речь превращалась в переведённые субтитры для аудитории: на выступлении, в прямой трансляции, на онлайн-презентации или уроке. Синтезированный голос не создаётся, и виртуальный аудиокабель не нужен. Субтитры появляются в плавающем окне, которое можно захватить в OBS или показать через демонстрацию экрана. Используется KizunaAI (на базе Soniox; движок Qwen работает так же). Снимки экрана сделаны в Sokuji 0.44.0 на Windows: речь на китайском с английскими субтитрами.',
  },
  steps: [
    {
      title: 'Настройте Sokuji с KizunaAI',
      content:
        'Если вы ещё этого не сделали, следуйте руководству <a href="/docs/kizuna-ai/quick-start">«Быстрый старт с KizunaAI»</a>, чтобы установить Sokuji и войти в учётную запись.',
    },
    {
      title: 'Выберите «Субтитры для моей речи»',
      content:
        'В мастере настройки выберите <strong>Субтитры для моей речи</strong>. Sokuji переходит в режим <strong>Я</strong> только с субтитрами. Если вы уже завершили настройку, нажмите <strong>Я</strong> в переключателе режимов в левом нижнем углу главного окна и в блоке <strong>Речь</strong> в настройках выключите <strong>Перевод, который слышит собеседник</strong>.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Задайте два языка',
      content:
        'Укажите в поле <strong>я говорю на</strong> язык, на котором вы говорите, а в поле <strong>собеседник читает</strong> — язык субтитров. Здесь: я говорю на китайском, собеседник читает на английском.',
      screenshot: '02-languages.png',
    },
    {
      title: 'Выберите микрофон',
      content: 'В настройках, в разделе <strong>Микрофон</strong>, выберите микрофон, в который вы говорите.',
      screenshot: '03-microphone.png',
    },
    {
      title: 'Попробуйте в главном окне',
      content:
        'Нажмите <strong>Старт</strong> и скажите несколько предложений. Sokuji показывает сказанное вами и его перевод в виде субтитров в главном окне. Вслух ничего не озвучивается.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Откройте окно субтитров',
      content:
        'Нажмите <strong>Субтитры</strong> в строке заголовка, чтобы превратить Sokuji в узкое окно субтитров. Переместите его и измените размер, затем нажмите <strong>Начать перевод</strong>. Кнопка <strong>Оба</strong> на его панели инструментов переключает показ обеих строк, только оригинала (<strong>Оригинал</strong>) или только перевода (<strong>Перевод</strong>).',
      screenshot: '05-subtitle-window.png',
    },
    {
      title: 'Покажите субтитры аудитории',
      content:
        'В OBS добавьте источник <strong>Захват окна</strong> и выберите окно субтитров Sokuji. В видеозвонке покажите свой экран или это окно. Аудитория видит субтитры, ничего не устанавливая.',
    },
  ],
  tips: {
    title: 'Советы для лучших результатов',
    items: [
      'Говорите законченными предложениями и делайте короткую паузу в конце каждого: перевод появляется, когда вы заканчиваете мысль.',
      'Показывайте только перевод («Перевод»), если ваша аудитория не читает на вашем языке.',
      'После выступления сохраните расшифровку кнопкой загрузки на панели инструментов.',
    ],
  },
  faq: {
    title: 'Часто задаваемые вопросы',
    items: [
      {
        question: 'Нужен ли для этого VB-CABLE?',
        answer: 'Нет. Виртуальный аудиокабель нужен только тогда, когда другие должны слышать переведённый голос. Здесь аудитория читает перевод.',
      },
      {
        question: 'Нужен ли Sokuji моей аудитории?',
        answer: 'Нет. Зрители видят субтитры в вашей трансляции, записи или на показанном вами экране.',
      },
      {
        question: 'Почему только субтитры, а не переведённый голос?',
        answer: 'Аудитория продолжает слышать ваш настоящий голос, а синтез речи стоит дополнительно. Если вам нужен голос, следуйте руководству о том, как добиться понимания на встрече.',
      },
    ],
  },
  troubleshooting: {
    title: 'Устранение неполадок',
    content:
      'Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Также можно задать вопрос в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const de: TutorialData = {
  pageTitle: 'Live übersetzte Untertitel für Ihre Vorträge, Streams und Präsentationen',
  backLink: 'Zurück zum KizunaAI-Schnellstart',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Übersicht',
    content:
      'Diese Anleitung richtet die Sokuji-Desktop-App so ein, dass sie das, was Sie sagen, in übersetzte Untertitel für Ihr Publikum verwandelt: bei einem Vortrag, einem Livestream, einer Online-Präsentation oder einer Unterrichtsstunde. Es wird keine synthetische Stimme erzeugt, und Sie brauchen kein virtuelles Audiokabel. Die Untertitel erscheinen in einem schwebenden Fenster, das OBS und die Bildschirmfreigabe erfassen können. Verwendet wird KizunaAI (Unterstützt von Soniox; die Qwen-Engine funktioniert genauso). Die Screenshots stammen aus Sokuji 0.44.0 unter Windows und zeigen Sprache auf Chinesisch mit englischen Untertiteln.',
  },
  steps: [
    {
      title: 'Sokuji mit KizunaAI einrichten',
      content:
        'Falls noch nicht geschehen, folgen Sie dem <a href="/docs/kizuna-ai/quick-start">KizunaAI-Schnellstart</a>, um Sokuji zu installieren und sich anzumelden.',
    },
    {
      title: '„Meine eigene Sprache untertiteln“ wählen',
      content:
        'Wählen Sie in der Einrichtung <strong>Meine eigene Sprache untertiteln</strong>. Damit wechselt Sokuji in den Modus <strong>Ich</strong>, nur mit Untertiteln. Wenn Sie die Einrichtung bereits abgeschlossen haben, klicken Sie im Modusschalter unten links im Hauptfenster auf <strong>Ich</strong> und schalten Sie im Bereich <strong>Sprache</strong> der Einstellungen <strong>Übersetzung, die das Gegenüber hört</strong> aus.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Die beiden Sprachen einstellen',
      content:
        'Stellen Sie bei <strong>Ich spreche</strong> die Sprache ein, in der Sie sprechen, und bei <strong>sie lesen</strong> die Sprache der Untertitel. Hier: Ich spreche Chinesisch, sie lesen Englisch.',
      screenshot: '02-languages.png',
    },
    {
      title: 'Mikrofon wählen',
      content: 'Wählen Sie in den Einstellungen unter <strong>Mikrofon</strong> das Mikrofon, in das Sie sprechen.',
      screenshot: '03-microphone.png',
    },
    {
      title: 'Im Hauptfenster ausprobieren',
      content:
        'Klicken Sie auf <strong>Starten</strong> und sagen Sie ein paar Sätze. Sokuji zeigt, was Sie gesagt haben, samt Übersetzung als Untertitel im Hauptfenster an. Es wird nichts laut gesprochen.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Das Untertitelfenster öffnen',
      content:
        'Klicken Sie in der Titelleiste auf <strong>Untertitel</strong>, um Sokuji in ein schmales Untertitelfenster zu verwandeln. Verschieben Sie es, ändern Sie seine Größe und klicken Sie dann auf <strong>Übersetzung starten</strong>. Die Schaltfläche <strong>Beides</strong> in seiner Symbolleiste wechselt zwischen beiden Zeilen, nur dem Original (<strong>Original</strong>) oder nur der Übersetzung (<strong>Übersetzung</strong>).',
      screenshot: '05-subtitle-window.png',
    },
    {
      title: 'Die Untertitel Ihrem Publikum zeigen',
      content:
        'Fügen Sie in OBS eine Quelle <strong>Fensteraufnahme</strong> hinzu und wählen Sie das Sokuji-Untertitelfenster. Geben Sie in einem Videoanruf Ihren Bildschirm oder dieses Fenster frei. Ihr Publikum sieht die Untertitel, ohne etwas zu installieren.',
    },
  ],
  tips: {
    title: 'Tipps für beste Ergebnisse',
    items: [
      'Sprechen Sie in ganzen Sätzen und machen Sie am Ende jedes Satzes eine kurze Pause: Die Übersetzung erscheint, sobald Sie einen Gedanken abgeschlossen haben.',
      'Zeigen Sie nur die Übersetzung („Übersetzung“), wenn Ihr Publikum Ihre Sprache nicht lesen kann.',
      'Speichern Sie das Transkript nach Ihrem Vortrag mit der Download-Schaltfläche in der Symbolleiste.',
    ],
  },
  faq: {
    title: 'Häufig gestellte Fragen',
    items: [
      {
        question: 'Brauche ich dafür VB-CABLE?',
        answer: 'Nein. Das virtuelle Audiokabel wird nur gebraucht, wenn andere eine übersetzte Stimme hören sollen. Hier liest Ihr Publikum die Übersetzung.',
      },
      {
        question: 'Braucht mein Publikum Sokuji?',
        answer: 'Nein. Es sieht die Untertitel in Ihrem Stream, Ihrer Aufzeichnung oder Ihrem freigegebenen Bildschirm.',
      },
      {
        question: 'Warum nur Untertitel statt einer übersetzten Stimme?',
        answer: 'Ihr Publikum hört weiterhin Ihre echte Stimme, und die Erzeugung von Sprache kostet extra. Wenn Sie lieber eine Stimme möchten, folgen Sie der Anleitung, um in einem Meeting verstanden zu werden.',
      },
    ],
  },
  troubleshooting: {
    title: 'Fehlerbehebung',
    content:
      'Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Sie können auch in den <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> fragen.',
  },
};

const uk: TutorialData = {
  pageTitle: 'Перекладені субтитри наживо для ваших виступів, трансляцій і презентацій',
  backLink: 'Назад до швидкого старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Огляд',
    content:
      'Цей посібник налаштовує настільний застосунок Sokuji так, щоб ваше мовлення перетворювалося на перекладені субтитри для аудиторії: на виступі, у прямій трансляції, на онлайн-презентації чи уроці. Синтезований голос не створюється, і віртуальний аудіокабель не потрібен. Субтитри з’являються в плаваючому вікні, яке можна захопити в OBS або показати через демонстрацію екрана. Використовується KizunaAI (на базі Soniox; рушій Qwen працює так само). Знімки екрана зроблено в Sokuji 0.44.0 на Windows: мовлення китайською з англійськими субтитрами.',
  },
  steps: [
    {
      title: 'Налаштуйте Sokuji з KizunaAI',
      content:
        'Якщо ви ще цього не зробили, дотримуйтеся посібника <a href="/docs/kizuna-ai/quick-start">«Швидкий старт із KizunaAI»</a>, щоб установити Sokuji та увійти в обліковий запис.',
    },
    {
      title: 'Виберіть «Субтитрувати власне мовлення»',
      content:
        'У майстрі налаштування виберіть <strong>Субтитрувати власне мовлення</strong>. Sokuji переходить у режим <strong>Я</strong> лише із субтитрами. Якщо ви вже завершили налаштування, натисніть <strong>Я</strong> в перемикачі режимів у лівому нижньому куті головного вікна й у блоці <strong>Мовлення</strong> налаштувань вимкніть <strong>Переклад, який чує співрозмовник</strong>.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Задайте дві мови',
      content:
        'Укажіть у полі <strong>Я говорю</strong> мову, якою ви говорите, а в полі <strong>вони читають</strong> — мову субтитрів. Тут: Я говорю китайською, вони читають англійською.',
      screenshot: '02-languages.png',
    },
    {
      title: 'Виберіть мікрофон',
      content: 'У налаштуваннях, у розділі <strong>Мікрофон</strong>, виберіть мікрофон, у який ви говорите.',
      screenshot: '03-microphone.png',
    },
    {
      title: 'Спробуйте в головному вікні',
      content:
        'Натисніть <strong>Почати</strong> і скажіть кілька речень. Sokuji показує сказане вами та його переклад у вигляді субтитрів у головному вікні. Уголос нічого не озвучується.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Відкрийте вікно субтитрів',
      content:
        'Натисніть <strong>Субтитри</strong> в рядку заголовка, щоб перетворити Sokuji на вузьке вікно субтитрів. Перемістіть його та змініть розмір, потім натисніть <strong>Почати переклад</strong>. Кнопка <strong>Обидва</strong> на його панелі інструментів перемикає показ обох рядків, лише оригіналу (<strong>Оригінал</strong>) або лише перекладу (<strong>Переклад</strong>).',
      screenshot: '05-subtitle-window.png',
    },
    {
      title: 'Покажіть субтитри аудиторії',
      content:
        'В OBS додайте джерело <strong>Захоплення вікна</strong> й виберіть вікно субтитрів Sokuji. У відеодзвінку покажіть свій екран або це вікно. Аудиторія бачить субтитри, нічого не встановлюючи.',
    },
  ],
  tips: {
    title: 'Поради для найкращих результатів',
    items: [
      'Говоріть завершеними реченнями й робіть коротку паузу в кінці кожного: переклад з’являється, коли ви завершуєте думку.',
      'Показуйте лише переклад («Переклад»), якщо ваша аудиторія не читає вашою мовою.',
      'Після виступу збережіть розшифровку кнопкою завантаження на панелі інструментів.',
    ],
  },
  faq: {
    title: 'Часті запитання',
    items: [
      {
        question: 'Чи потрібен для цього VB-CABLE?',
        answer: 'Ні. Віртуальний аудіокабель потрібен лише тоді, коли інші мають чути перекладений голос. Тут аудиторія читає переклад.',
      },
      {
        question: 'Чи потрібен Sokuji моїй аудиторії?',
        answer: 'Ні. Глядачі бачать субтитри у вашій трансляції, записі або на показаному вами екрані.',
      },
      {
        question: 'Чому лише субтитри, а не перекладений голос?',
        answer: 'Аудиторія й далі чує ваш справжній голос, а синтез мовлення коштує додатково. Якщо вам потрібен голос, дотримуйтеся посібника про те, як бути зрозумілим на зустрічі.',
      },
    ],
  },
  troubleshooting: {
    title: 'Усунення несправностей',
    content:
      'Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Також можна запитати в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const it: TutorialData = {
  pageTitle: 'Sottotitoli tradotti dal vivo per i tuoi interventi, le dirette e le presentazioni',
  backLink: 'Torna alla guida rapida a KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Panoramica',
    content:
      'Questa guida configura l’app desktop di Sokuji per trasformare ciò che dici in sottotitoli tradotti per il tuo pubblico: un intervento, una diretta, una presentazione online o una lezione. Non viene generata alcuna voce sintetica, e non ti serve un cavo audio virtuale. I sottotitoli compaiono in una finestra mobile che OBS e la condivisione dello schermo possono catturare. Usa KizunaAI (Con tecnologia Soniox; il motore Qwen funziona allo stesso modo). Le schermate provengono da Sokuji 0.44.0 su Windows, parlando in cinese con sottotitoli in inglese.',
  },
  steps: [
    {
      title: 'Configura Sokuji con KizunaAI',
      content:
        'Se non l’hai ancora fatto, segui la <a href="/docs/kizuna-ai/quick-start">guida rapida a KizunaAI</a> per installare Sokuji e accedere.',
    },
    {
      title: 'Scegli “Sottotitola il mio parlato”',
      content:
        'Nella configurazione guidata, scegli <strong>Sottotitola il mio parlato</strong>. Sokuji passa in modalità <strong>Io</strong>, solo con i sottotitoli. Se hai già completato la configurazione, fai clic su <strong>Io</strong> nel selettore di modalità in basso a sinistra nella finestra principale e, nella sezione <strong>Voce</strong> delle Impostazioni, disattiva <strong>Traduzione che sente l\'altra parte</strong>.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Imposta le due lingue',
      content:
        'Imposta <strong>Io parlo</strong> sulla lingua in cui parli e <strong>loro leggono</strong> sulla lingua dei sottotitoli. Qui: Io parlo cinese, loro leggono inglese.',
      screenshot: '02-languages.png',
    },
    {
      title: 'Scegli il microfono',
      content: 'In Impostazioni, in <strong>Microfono</strong>, scegli il microfono in cui parli.',
      screenshot: '03-microphone.png',
    },
    {
      title: 'Provalo nella finestra principale',
      content:
        'Fai clic su <strong>Avvia</strong> e pronuncia qualche frase. Sokuji mostra ciò che hai detto e la sua traduzione come sottotitoli nella finestra principale. Nulla viene pronunciato ad alta voce.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Apri la finestra dei sottotitoli',
      content:
        'Fai clic su <strong>Sottotitoli</strong> nella barra del titolo per trasformare Sokuji in una sottile finestra di sottotitoli. Spostala e ridimensionala, poi fai clic su <strong>Avvia la traduzione</strong>. Il pulsante <strong>Entrambi</strong> nella sua barra degli strumenti alterna tra entrambe le righe, solo l’originale (<strong>Originale</strong>) o solo la traduzione (<strong>Traduzione</strong>).',
      screenshot: '05-subtitle-window.png',
    },
    {
      title: 'Mostra i sottotitoli al tuo pubblico',
      content:
        'In OBS, aggiungi una fonte <strong>Cattura finestra</strong> e scegli la finestra dei sottotitoli di Sokuji. In una videochiamata, condividi lo schermo o quella finestra. Il tuo pubblico vede i sottotitoli senza installare nulla.',
    },
  ],
  tips: {
    title: 'Consigli per risultati migliori',
    items: [
      'Parla con frasi complete e fai una breve pausa alla fine di ciascuna: la traduzione compare quando concludi un pensiero.',
      'Mostra solo la traduzione (Traduzione) se il tuo pubblico non legge la tua lingua.',
      'Dopo il tuo intervento, usa il pulsante di download nella barra degli strumenti per salvare la trascrizione.',
    ],
  },
  faq: {
    title: 'Domande frequenti',
    items: [
      {
        question: 'Mi serve VB-CABLE?',
        answer: 'No. Il cavo audio virtuale serve solo quando gli altri devono sentire una voce tradotta. Qui il tuo pubblico legge la traduzione.',
      },
      {
        question: 'Il mio pubblico ha bisogno di Sokuji?',
        answer: 'No. Vede i sottotitoli nella tua diretta, nella tua registrazione o nello schermo che condividi.',
      },
      {
        question: 'Perché solo sottotitoli invece di una voce tradotta?',
        answer: 'Il tuo pubblico continua a sentire la tua voce reale, e generare il parlato costa di più. Se preferisci una voce, segui la guida per farsi capire in una riunione.',
      },
    ],
  },
  troubleshooting: {
    title: 'Risoluzione dei problemi',
    content:
      'Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Puoi anche chiedere su <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function SubtitleMySpeechTutorial() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/tutorials/subtitle-my-speech" />;
}
